import type { OnboardingStatusDto } from '@osteopro/shared';
import type { QueryClient } from '@tanstack/react-query';
import { onboardingKeys } from './query-keys.js';

/**
 * La première facture du cabinet est déjà décidée par la mise en route : l'étape `first_invoice`
 * est faite dès qu'une facture non brouillon existe. On ne tient pas un second compteur.
 *
 * Le drapeau est lu au succès de l'émission, puis marqué fait dans le cache. Une émission refusée
 * (mentions manquantes) n'atteint pas ce point : la célébration reste disponible pour la
 * confirmation. Les factures suivantes lisent l'étape déjà faite et ne rejouent rien.
 */

export interface FirstInvoiceClaim {
  readonly claimed: boolean;
}

interface Celebration {
  readonly id: number;
  readonly number: string | null;
}

type CelebrationListener = (celebration: Celebration) => void;

let nextCelebrationId = 0;
const listeners = new Set<CelebrationListener>();

function withFirstInvoiceDone(status: OnboardingStatusDto): OnboardingStatusDto {
  return {
    ...status,
    steps: status.steps.map((step) =>
      step.key === 'first_invoice' ? { ...step, done: true } : step,
    ),
  };
}

/** Vrai une seule fois : l'étape existe et n'était pas encore faite. Le cache passe alors à fait. */
export function claimFirstInvoice(queryClient: QueryClient): FirstInvoiceClaim {
  const status = queryClient.getQueryData<OnboardingStatusDto>(onboardingKeys.status());
  if (status === undefined) return { claimed: false };
  const step = status.steps.find((item) => item.key === 'first_invoice');
  if (step === undefined || step.done) return { claimed: false };
  queryClient.setQueryData(onboardingKeys.status(), withFirstInvoiceDone(status));
  return { claimed: true };
}

/**
 * Un refetch parti avant l'émission peut reposer l'étape à « pas faite ». On réaffirme le drapeau
 * que le succès vient de consommer, pour que la facture suivante ne célèbre pas.
 */
export function keepFirstInvoiceClaimed(queryClient: QueryClient): void {
  const status = queryClient.getQueryData<OnboardingStatusDto>(onboardingKeys.status());
  if (status === undefined) return;
  const step = status.steps.find((item) => item.key === 'first_invoice');
  if (step === undefined || step.done) return;
  queryClient.setQueryData(onboardingKeys.status(), withFirstInvoiceDone(status));
}

export function celebrateFirstInvoiceIfClaimed(
  claim: FirstInvoiceClaim,
  number: string | null,
): void {
  if (!claim.claimed) return;
  const celebration: Celebration = { id: nextCelebrationId, number };
  nextCelebrationId += 1;
  for (const listener of listeners) listener(celebration);
}

export function subscribeFirstInvoiceCelebration(listener: CelebrationListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
